import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import '../Style/Sidebar.css';
import companyLogo from '../assets/CompanyLogo.png';
import {
  FaClipboardList,
  FaPlusSquare,
  FaFlask,
  FaBug,
  FaChartBar,
  FaUserShield,
  FaSignOutAlt,
  FaBars,
  FaTimes,
  FaChevronDown,
  FaChevronUp,
  FaTruck,           // ← NEW icon
  FaClock,           // ← NEW: clock-in icon
} from 'react-icons/fa';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';

export default function Sidebar() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);          // mobile drawer toggle
  const [userRole, setUserRole] = useState(null);   // null = unknown
  const [displayName, setDisplayName] = useState('');
  const [authReady, setAuthReady] = useState(false);
  const [expCreate, setExpCreate] = useState(
    () => localStorage.getItem('sb_create_open') === '0' ? false : true
  );

  /* Fetch role & name on auth change */
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setUserRole('');
        setDisplayName('');
        setAuthReady(true);
        return;
      }
      try {
        const snap = await getDoc(doc(db, 'users', user.uid));
        if (snap.exists()) {
          const data = snap.data();
          const r = (data.role || '').trim();
          setUserRole(r);
          const fullName = [data.firstName, data.lastName].filter(Boolean).join(' ');
          setDisplayName(fullName || (data.username || user.email || 'User'));
        } else {
          setUserRole('');
          setDisplayName(user.email || 'User');
        }
      } catch (err) {
        console.error('Failed to load user role:', err);
        setUserRole('');
      } finally {
        setAuthReady(true);
      }
    });
    return () => unsub();
  }, []);

  const toggleCreateGroup = () => {
    setExpCreate(p => {
      localStorage.setItem('sb_create_open', p ? '0' : '1');
      return !p;
    });
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (err) {
      console.error(err);
    }
  };

  const handleNavClick = () => setOpen(false);

  // ----- Role helpers -----
  const normalizeRole = (r) => (r || '').toString().trim().toLowerCase().replace(/\s+/g, '-');
  const roleKey = normalizeRole(userRole);
  const isAdmin = roleKey === 'admin';
  const isFinancialManager = ['financial-manager', 'finance-manager'].includes(roleKey);
  const canSeePayroll = isAdmin || isFinancialManager;

  const toggleDrawerKey = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setOpen(o => !o);
    }
  }, []);

  return (
    <>
      {/* Mobile Hamburger */}
      <button
        className="burger-btn"
        aria-label="Toggle sidebar navigation"
        aria-expanded={open}
        onClick={() => setOpen(p => !p)}
        onKeyDown={toggleDrawerKey}
      >
        {open ? <FaTimes /> : <FaBars />}
      </button>

      <aside className={`sidebar ${open ? 'show' : ''}`} aria-label="Primary sidebar">
        {/* Company / Brand */}
        <div className="sidebar-header">
          <div className="logo-wrapper">
            <img
              src={companyLogo}
              alt="Nyabanga Engineering"
              className="company-logo"
              draggable="false"
            />
          </div>
          <h1 className="company-name">Nyabanga Engineering</h1>

          <div className="company-contact">
            Plot 35, Pretoria Rd, Benoni<br />
            011 420-1537 &nbsp;|&nbsp; 082 471-9512<br />
            <a href="mailto:info@nyabanga.co.za">info@nyabanga.co.za</a>
          </div>
        </div>

        {/* User Panel */}
        {authReady && (
          <div className="user-mini-panel" aria-label="Signed in user">
            <div className="avatar-fallback" aria-hidden="true">
              {displayName?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="user-lines">
              <span className="u-name" title={displayName}>{displayName || 'User'}</span>
              {userRole && (
                <span className={`u-role chip role-${normalizeRole(userRole)}`}>
                  {userRole}
                </span>
              )}
            </div>
          </div>
        )}

        <hr className="divider" />

        {/* Navigation */}
        <nav className="nav-items" onClick={handleNavClick}>
          <NavLink
            to="/Jobs"
            className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}
          >
            <span className="active-indicator" aria-hidden="true" />
            <FaClipboardList className="icon" />
            <span>Job Cards</span>
          </NavLink>

          {/* Collapsible "Create" group */}
          <div className="nav-group">
            <button
              type="button"
              className="nav-group-toggle"
              onClick={toggleCreateGroup}
              aria-expanded={expCreate}
              aria-controls="create-group"
            >
              <div className="left">
                <FaPlusSquare className="icon" />
                <span>Create</span>
              </div>
              {expCreate ? <FaChevronUp className="chev" /> : <FaChevronDown className="chev" />}
            </button>
            <div
              id="create-group"
              className={`group-inner ${expCreate ? 'open' : ''}`}
            >
              <NavLink
                to="/CreateJob"
                className={({ isActive }) => isActive ? 'nav-sub active' : 'nav-sub'}
              >
                <span className="active-indicator" aria-hidden="true" />
                <span className="sub-bullet" />
                <span>New Job</span>
              </NavLink>

              <NavLink
                to="/CreateRFQ"
                className={({ isActive }) => isActive ? 'nav-sub active' : 'nav-sub'}
              >
                <span className="active-indicator" aria-hidden="true" />
                <span className="sub-bullet" />
                <span>New RFQ</span>
              </NavLink>

              {/* NEW: Collection & Delivery right under New RFQ */}
              <NavLink
                to="/CollectionAndDelivery"
                className={({ isActive }) => isActive ? 'nav-sub active' : 'nav-sub'}
              >
                <span className="active-indicator" aria-hidden="true" />
                <span className="sub-bullet" />
                <span>Collection &amp; Delivery</span>
              </NavLink>

              <NavLink
                to="/TestReport"
                className={({ isActive }) => isActive ? 'nav-sub active' : 'nav-sub'}
              >
                <span className="active-indicator" aria-hidden="true" />
                <span className="sub-bullet" />
                <span>Test Report</span>
              </NavLink>

              <NavLink
                to="/FailureReport"
                className={({ isActive }) => isActive ? 'nav-sub active' : 'nav-sub'}
              >
                <span className="active-indicator" aria-hidden="true" />
                <span className="sub-bullet" />
                <span>Failure Report</span>
              </NavLink>

              <NavLink
                to="/AddProduct"
                className={({ isActive }) => isActive ? 'nav-sub active' : 'nav-sub'}
              >
                <span className="active-indicator" aria-hidden="true" />
                <span className="sub-bullet" />
                <span>Add Product</span>
              </NavLink>

              {/* >>> NEW: Payroll (visible only to Admin or Financial-Manager) <<< */}
              {authReady && canSeePayroll && (
                <NavLink
                  to="/Payroll"
                  className={({ isActive }) => isActive ? 'nav-sub active' : 'nav-sub'}
                >
                  <span className="active-indicator" aria-hidden="true" />
                  <span className="sub-bullet" />
                  <span>Payroll</span>
                </NavLink>
              )}
              {/* Note: also enforce access control in your /Payroll route or page component. */}
            </div>
          </div>

          {/* NEW: Clock In (placed above Notifications) */}
          <NavLink
            to="/ClockIn"
            className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}
          >
            <span className="active-indicator" aria-hidden="true" />
            <FaClock className="icon" />
            <span>Clock In</span>
          </NavLink>

          <NavLink
            to="/Notifications"
            className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}
          >
            <span className="active-indicator" aria-hidden="true" />
            <FaBug className="icon" />
            <span>Notifications</span>
          </NavLink>

          <NavLink
            to="/Report"
            className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}
          >
            <span className="active-indicator" aria-hidden="true" />
            <FaChartBar className="icon" />
            <span>Reports</span>
          </NavLink>

          {authReady && isAdmin && (
            <NavLink
              to="/AdminSection"
              className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}
            >
              <span className="active-indicator" aria-hidden="true" />
              <FaUserShield className="icon" />
              <span>Admin Section</span>
            </NavLink>
          )}
        </nav>

        {/* Logout */}
        <button
          type="button"
          className="logout-btn"
          onClick={handleLogout}
          aria-label="Sign out"
        >
          <FaSignOutAlt className="icon" />
          <span>Logout</span>
        </button>
      </aside>
    </>
  );
}
